// ----- Wave E.2 — PackageTab ----- //
//
// Shows how the document will appear in the compiled eCTD package.
// Per guardrail #9, the canonical V1 renaming + folder-structure rules
// live server-side (Compiler backend — Wave E.7). This phase shows a
// best-effort preview computed entirely from the document's existing
// fields:
//
//   - File name: pass-through (V2 hasn't applied any renaming yet)
//   - Folder path: derived from CtdSectionCode via splitOnDots →
//     m{N} → 3.2 → 3.2.S → ... → fileName
//
// When Wave E.7 lands the real renamer, callers can swap this out for
// a new `GET /api/v1/documents/:id/package-preview` endpoint without
// changing the consumer's call site (this is presentational only).

import { type ReactElement } from 'react';

export interface PackageTabProps {
  readonly fileName: string | null;
  readonly ctdSectionCode: string | null;
  readonly moduleNumber: string | null;
}

export function PackageTab({
  fileName,
  ctdSectionCode,
  moduleNumber,
}: PackageTabProps): ReactElement {
  const path = computeFolderPath(ctdSectionCode, moduleNumber);

  return (
    <div data-testid="package-tab" style={{ padding: '8px 4px', fontSize: 13 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: '#6B7280',
          marginBottom: 4,
        }}
      >
        File name in package
      </div>
      <div data-testid="package-file-name" style={{ fontWeight: 600, marginBottom: 12 }}>
        {fileName ?? '(unknown)'}
      </div>

      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: '#6B7280',
          marginBottom: 4,
        }}
      >
        Folder path
      </div>
      <div
        data-testid="package-folder-path"
        style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 12,
          background: '#F3F4F6',
          padding: '8px 10px',
          borderRadius: 4,
          wordBreak: 'break-all',
        }}
      >
        {path.length === 0
          ? '(unassigned — set placement first)'
          : path.join(' → ')}
        {path.length > 0 && fileName ? ` → ${fileName}` : ''}
      </div>

      <div
        data-testid="package-disclaimer"
        style={{ fontSize: 11, color: '#6B7280', marginTop: 12, fontStyle: 'italic' }}
      >
        Canonical filename + path will be assembled by the Compiler
        backend (Wave E.7). This is a best-effort preview from the
        document's current fields.
      </div>
    </div>
  );
}

// Split a CTD section code like "3.2.S.4.1" into prefix paths suitable
// for an eCTD folder hierarchy: ['m3', '3.2', '3.2.S', '3.2.S.4',
// '3.2.S.4.1']. Falls back to [] if input is empty.
function computeFolderPath(
  ctdSectionCode: string | null,
  moduleNumber: string | null,
): string[] {
  if (!ctdSectionCode) return [];
  const parts = ctdSectionCode.split('.');
  const out: string[] = [];
  // Module prefix — prefer the explicit ModuleNumber column if set;
  // otherwise infer from the first part of the section code.
  const modPart = moduleNumber ?? parts[0] ?? '';
  if (modPart !== '') out.push(`m${modPart}`);
  for (let i = 2; i <= parts.length; i += 1) {
    out.push(parts.slice(0, i).join('.'));
  }
  return out;
}
